/**
 * Speed Role - Cast Pillar
 *
 * Normaliza input sujo para número tipado.
 * Detecta unidades em strings e converte se necessário.
 *
 * @example
 * import { castSpeed, castSpeedStrict, detectSpeedUnit } from '@attrx/role-morphic/speed/cast';
 *
 * castSpeed('100 km/h', 'meter_per_second');   // 27.7778... (converte)
 * castSpeed('60 mph', 'kilometer_per_hour');   // 96.5606... (converte)
 * castSpeedStrict('100 km/h');                 // 100 (não converte)
 * detectSpeedUnit('60 miles per hour');        // 'mile_per_hour'
 */

import { SPEED_UNITS, SPEED_ALIASES, type SpeedUnit } from "./constants";
import { convertSpeed } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, detectando e convertendo unidade se presente
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo para conversão
 * @returns Número ou null se não puder converter
 *
 * @example
 * castSpeed(100, 'meter_per_second');              // 100
 * castSpeed('100', 'meter_per_second');            // 100
 * castSpeed('100 km/h', 'meter_per_second');       // 27.7778... (converte)
 * castSpeed('60 mph', 'kilometer_per_hour');       // 96.5606... (converte)
 * castSpeed('invalid', 'meter_per_second');        // null
 */
export function castSpeed(
  input: unknown,
  targetVariant: SpeedUnit = "meter_per_second"
): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    return parseSpeedString(input, targetVariant);
  }

  return null;
}

/**
 * Faz cast sem conversão de unidade (só parseia o valor literal)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castSpeedStrict('100 km/h');   // 100 (ignora unidade, não converte)
 * castSpeedStrict('100');        // 100
 * castSpeedStrict(100);          // 100
 */
export function castSpeedStrict(input: unknown): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    const numValue = parseNumber(input);
    return numValue;
  }

  return null;
}

/**
 * Versão safe de castSpeed que retorna Result
 */
export function tryCastSpeed(
  input: unknown,
  targetVariant: SpeedUnit = "meter_per_second"
): Result<number> {
  const result = castSpeed(input, targetVariant);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to speed:${targetVariant}`,
    };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECTION
// =============================================================================

/**
 * Detecta unidade de velocidade a partir de uma string
 *
 * @param input - String com possível unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectSpeedUnit('100 km/h');           // 'kilometer_per_hour'
 * detectSpeedUnit('60 mph');             // 'mile_per_hour'
 * detectSpeedUnit('10 knots');           // 'knot'
 * detectSpeedUnit('100');                // null
 */
export function detectSpeedUnit(input: string): SpeedUnit | null {
  const trimmed = input.trim().toLowerCase();
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);

  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  return detectUnit(unitStr);
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Parseia string para número, detectando unidade e convertendo se necessário
 */
function parseSpeedString(input: string, targetVariant: SpeedUnit): number | null {
  const trimmed = input.trim().toLowerCase();

  // Extrai número e possível unidade
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const [, numStr, unitStr] = match;

  // Normaliza número
  const numValue = parseNumber(numStr);
  if (numValue === null) {
    return null;
  }

  // Se tem unidade no input, converte se necessário
  const cleanUnitStr = unitStr.trim();
  if (cleanUnitStr) {
    const detectedUnit = detectUnit(cleanUnitStr);
    if (detectedUnit && detectedUnit !== targetVariant) {
      // Converte da unidade detectada para a target
      return convertSpeed(detectedUnit, targetVariant, numValue);
    }
  }

  return numValue;
}

/**
 * Parseia string numérica considerando formatos brasileiro e americano
 */
function parseNumber(numStr: string): number | null {
  // Remove espaços
  numStr = numStr.replace(/\s/g, "");

  const lastComma = numStr.lastIndexOf(",");
  const lastDot = numStr.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      // Brasileiro: 1.000,50
      numStr = numStr.replace(/\./g, "").replace(",", ".");
    } else {
      // Americano: 1,000.50
      numStr = numStr.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Só vírgulas: detecta se é milhar ou decimal
    const afterComma = numStr.split(",").slice(1);
    const isThousandSep = afterComma.every((part) => part.length === 3);
    if (isThousandSep) {
      numStr = numStr.replace(/,/g, "");
    } else {
      numStr = numStr.replace(",", ".");
    }
  } else if (!hasComma && hasDot) {
    // Só pontos: múltiplos pontos = milhar brasileiro
    const afterDot = numStr.split(".").slice(1);
    if (afterDot.length > 1) {
      numStr = numStr.replace(/\./g, "");
    }
  }

  const value = parseFloat(numStr);
  return isNaN(value) ? null : value;
}

/**
 * Detecta unidade a partir de string (símbolo ou alias)
 */
function detectUnit(unitStr: string): SpeedUnit | null {
  const normalized = unitStr.toLowerCase().trim();

  // Checa aliases primeiro
  if (normalized in SPEED_ALIASES) {
    return SPEED_ALIASES[normalized] as SpeedUnit;
  }

  // Checa símbolos e nomes
  for (const [variant, config] of Object.entries(SPEED_UNITS)) {
    if (
      config.symbol.toLowerCase() === normalized ||
      config.singular?.toLowerCase() === normalized ||
      config.plural?.toLowerCase() === normalized ||
      variant.toLowerCase() === normalized
    ) {
      return variant as SpeedUnit;
    }
  }

  return null;
}
